import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Video, Plus, Trash2, Clock, Download, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";

const TEMPLATES = [
  { key: "neon", name: "Neon Music" }, { key: "minimal", name: "Minimal Lyrics" },
  { key: "dark_studio", name: "Dark Studio" }, { key: "sound_waves", name: "Animated Sound Waves" },
  { key: "rap", name: "Rap Video" }, { key: "rnb", name: "R&B Mood" },
  { key: "cinematic", name: "Cinematic Lyrics" }, { key: "retro", name: "Retro Visualizer" },
];
const ASPECTS = [{ k: "9:16", l: "Vertical (TikTok/Reels)" }, { k: "16:9", l: "Landscape (YouTube)" }, { k: "1:1", l: "Square (Posts)" }];

export function LyricVideoModal({ open, onClose, song }) {
  const { progress } = usePlayer();
  const [lines, setLines] = useState([]);
  const [template, setTemplate] = useState("neon");
  const [aspect, setAspect] = useState("9:16");
  const [quality, setQuality] = useState("720p");
  const [branding, setBranding] = useState(true);
  const [job, setJob] = useState(null);
  const [rendering, setRendering] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    if (open && song) {
      const raw = (song.lyrics || "").split("\n").map((t) => t.trim()).filter(Boolean);
      setLines(raw.length ? raw.map((t, i) => ({ time: i * 4, text: t })) : [{ time: 0, text: "" }]);
      setJob(null); setRendering(false);
    }
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [open, song]);

  if (!song) return null;

  const addLine = () => setLines([...lines, { time: Math.round(progress || (lines.length * 4)), text: "" }]);
  const stampLine = (i) => setLines(lines.map((l, idx) => idx === i ? { ...l, time: Math.round(progress) } : l));
  const update = (i, field, v) => setLines(lines.map((l, idx) => idx === i ? { ...l, [field]: field === "time" ? Number(v) : v } : l));
  const remove = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const render = async () => {
    const clean = lines.filter((l) => l.text.trim());
    if (!clean.length) return toast.error("Please add or confirm the lyrics first. JixxeoX won't invent lyrics.");
    setRendering(true);
    try {
      const { data } = await api.post("/lyric-videos", {
        song_id: song.id, lines: clean, template, aspect, quality, show_branding: branding, use_cover_bg: true,
      });
      setJob({ id: data.video_id, status: "processing" });
      pollRef.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/lyric-videos/${data.video_id}`);
          setJob(s.video);
          if (s.video.status !== "processing") { clearInterval(pollRef.current); setRendering(false); }
        } catch { clearInterval(pollRef.current); setRendering(false); }
      }, 3000);
    } catch (e) { toast.error(errMsg(e)); setRendering(false); }
  };

  const downloadVideo = async () => {
    try {
      const res = await api.get(`/lyric-videos/${job.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(song.title || "video").replace(/[^a-z0-9 \-_]/gi, "")} - JixxeoX.mp4`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="jx-glass border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lyric-video-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Video className="text-cyan-400" size={20} /> JixxeoX Lyric Video — {song.title}</DialogTitle></DialogHeader>

        {job && job.status === "ready" ? (
          <div className="text-center py-6 space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-500/15 grid place-items-center mx-auto"><Check className="text-green-400" size={30} /></div>
            <p className="font-semibold">Your lyric video is ready!</p>
            <button onClick={downloadVideo} data-testid="download-lyric-video-btn" className="jx-cta text-white font-semibold px-6 py-3 rounded-xl inline-flex items-center gap-2 mx-auto">
              <Download size={18} /> Download Video ({aspect} • {quality})
            </button>
          </div>
        ) : job && job.status === "failed" ? (
          <div className="text-center py-6 space-y-3">
            <p className="text-rose-400 font-semibold">Render failed</p>
            <p className="text-sm text-slate-400">{job.error || "Please try again."}</p>
            <button onClick={() => setJob(null)} className="text-sm text-purple-400">Try again</button>
          </div>
        ) : rendering ? (
          <div className="text-center py-8 space-y-3">
            <Loader2 className="animate-spin text-cyan-400 mx-auto" size={36} />
            <p className="font-semibold">Rendering your video…</p>
            <p className="text-sm text-slate-400">This runs on our servers and may take a minute. We'll notify you when it's done.</p>
          </div>
        ) : (
          <>
            <div>
              <p className="text-xs font-mono uppercase tracking-wider text-purple-400 mb-2">Template</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TEMPLATES.map((t) => (
                  <button key={t.key} onClick={() => setTemplate(t.key)} data-testid={`tpl-${t.key}`}
                    className={`text-xs py-2 px-2 rounded-lg border transition-colors ${template === t.key ? "jx-cta text-white border-transparent" : "border-white/10 text-slate-300"}`}>{t.name}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-purple-400 mb-2">Format</p>
                <select value={aspect} onChange={(e) => setAspect(e.target.value)} data-testid="aspect-select" className="w-full bg-black/40 border border-white/10 rounded-lg p-2 text-sm">
                  {ASPECTS.map((a) => <option key={a.k} value={a.k}>{a.l}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-purple-400 mb-2">Quality</p>
                <select value={quality} onChange={(e) => setQuality(e.target.value)} data-testid="quality-select" className="w-full bg-black/40 border border-white/10 rounded-lg p-2 text-sm">
                  <option value="720p">720p</option><option value="1080p">1080p</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input type="checkbox" checked={branding} onChange={(e) => setBranding(e.target.checked)} /> Show JixxeoX branding
            </label>

            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-mono uppercase tracking-wider text-purple-400">Lyrics & Timing</p>
                <span className="text-[11px] text-slate-500">Play the song, then tap the clock to stamp timing</span>
              </div>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                {lines.map((l, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input type="number" value={l.time} onChange={(e) => update(i, "time", e.target.value)} className="w-16 bg-black/30 border-white/10 text-xs" />
                    <button onClick={() => stampLine(i)} title="Stamp current playback time" className="p-2 text-slate-400 hover:text-cyan-400"><Clock size={15} /></button>
                    <Input value={l.text} onChange={(e) => update(i, "text", e.target.value)} placeholder="Lyric line…" data-testid={`lyric-line-${i}`} className="flex-1 bg-black/30 border-white/10 text-sm" />
                    <button onClick={() => remove(i)} className="p-2 text-slate-500 hover:text-rose-400"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
              <button onClick={addLine} className="mt-2 text-sm text-purple-400 flex items-center gap-1"><Plus size={14} /> Add line</button>
            </div>

            <button onClick={render} data-testid="render-lyric-video-btn" className="jx-cta text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2">
              <Video size={18} /> Create Lyric Video
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
